import { useState, useEffect } from "react";
import { generateJSON, isOpenAIConfigured } from "../services/openaiClient.js";
import { supabase } from "../lib/supabase.js";
import ExportPanel from "../components/resume/ExportPanel";
import { useAuth } from "../contexts/AuthContext";
import FeatureAccessIndicator from "../components/ui/FeatureAccessIndicator";
import { loadAssessmentProgress } from "../services/assessmentService.js";
import { occupationService } from "../services/occupationService.js";
import Header from "../components/ui/Header";
import Icon from "../components/AppIcon";
import AdSense from "../components/AdSense";

type StarEntry = {
  id: string;
  situation: string;
  task: string;
  action: string;
  result: string;
  metrics?: string;
  created_at: string;
};

const emptyKit = {
  summary_variants: [] as Array<any>,
  keyword_bank: [] as Array<any>,
  skills: [] as Array<any>,
  achievements: [] as Array<any>,
  experience: [] as Array<any>,
  links: [] as Array<any>,
  star_entries: [] as StarEntry[],
  skills_catalog: [] as Array<{ group: string; name: string; level?: number }>
};

export default function ResumeWizard() {
  const { user, isPremium, userProfile } = useAuth();
  const [kit, setKit] = useState(emptyKit);
  const [occupation, setOccupation] = useState("");
  const [level, setLevel] = useState("Mid");
  const [targetRole, setTargetRole] = useState("");
  const [zip, setZip] = useState("");
  const [wins, setWins] = useState("");
  const [isLoadingUserData, setIsLoadingUserData] = useState(true);

  const [loading, setLoading] = useState(null);

  // STAR form state
  const [starForm, setStarForm] = useState<{ situation: string; task: string; action: string; result: string; metrics: string }>({ situation: "", task: "", action: "", result: "", metrics: "" });
  const [editingStarId, setEditingStarId] = useState<string | null>(null);

  // Skills selection state
  const [skillsFilter, setSkillsFilter] = useState("");
  const [skillsGroupFilter, setSkillsGroupFilter] = useState<string | null>(null);

  // Load user's assessment data to pre-populate fields
  useEffect(() => {
    const loadUserAssessmentData = async () => {
      if (!user || !isPremium) {
        setIsLoadingUserData(false);
        return;
      }

      try {
        // Load existing resume kit first
        const { data: savedKit } = await supabase
          .from("resume_kits")
          .select("*")
          .eq("user_id", user.id)
          .order("updated_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (savedKit) {
          // Normalize structure and defaults
          setKit({
            summary_variants: savedKit.summary_variants || [],
            keyword_bank: savedKit.keyword_bank || [],
            skills: savedKit.skills || [],
            achievements: savedKit.achievements || [],
            experience: savedKit.experience || [],
            links: savedKit.links || [],
            star_entries: savedKit.star_entries || [],
            skills_catalog: savedKit.skills_catalog || []
          });
        }

        // Load assessment data from database
        const assessmentResult = await loadAssessmentProgress(user.id);
        
        if (assessmentResult.success && assessmentResult.data) {
          const assessmentData = assessmentResult.data;
          const assessmentSetup = assessmentData.assessment_setup;
          
          if (assessmentSetup) {
            // Pre-populate occupation
            if (assessmentSetup.occupation) {
              setOccupation(assessmentSetup.occupation);
            }
            
            // Pre-populate experience level
            if (assessmentSetup.seniority) {
              // Map assessment seniority to resume wizard level
              const levelMapping = {
                'entry': 'Entry',
                'junior': 'Entry', 
                'mid': 'Mid',
                'senior': 'Senior',
                'executive': 'Executive',
                'freelance': 'Mid'
              };
              setLevel(levelMapping[assessmentSetup.seniority] || 'Mid');
            }
            
            // Pre-populate ZIP code
            if (assessmentSetup.currentZipCode) {
              setZip(assessmentSetup.currentZipCode);
            }
            
            // Pre-populate target role if available
            if (assessmentSetup.targetRole) {
              setTargetRole(assessmentSetup.targetRole);
            }
          }
        }
        
        // Also try to get data from user profile if available
        if (userProfile?.occupation_id) {
          try {
            const occupationData = await occupationService.getOccupationById(userProfile.occupation_id);
            if (occupationData && !occupation) {
              setOccupation(occupationData.title);
            }
          } catch (error) {
            console.warn('Could not load occupation from profile:', error);
          }
        }
        
        if (userProfile?.seniority_level && !level) {
          const levelMapping = {
            'entry': 'Entry',
            'junior': 'Entry',
            'mid': 'Mid', 
            'senior': 'Senior',
            'executive': 'Executive',
            'freelance': 'Mid'
          };
          setLevel(levelMapping[userProfile.seniority_level] || 'Mid');
        }
        
      } catch (error) {
        console.error('Error loading user assessment data:', error);
      } finally {
        setIsLoadingUserData(false);
      }
    };

    loadUserAssessmentData();
  }, [user, isPremium, userProfile]);

  // Loading state
  if (isLoadingUserData) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-8 space-y-8">
        <header className="text-center">
          <h1 className="text-3xl font-semibold mb-4">Resume Content Kit</h1>
          <p className="text-gray-600 mb-8">Loading your assessment data...</p>
        </header>
        
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  // Premium access check
  if (!isPremium) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-8 space-y-8">
        <header className="text-center">
          <h1 className="text-3xl font-semibold mb-4">Resume Content Kit</h1>
          <p className="text-gray-600 mb-8">Generate summaries, keywords, and achievement bullets tailored to your role.</p>
        </header>
        
        <div className="flex justify-center">
          <FeatureAccessIndicator 
            user={user} 
            feature="premium-exercises"
            showUpgradePrompt={true}
            size="lg"
            className="max-w-md"
          />
        </div>
      </div>
    );
  }

  async function makeSummaries() {
    setLoading("Summaries…");
    const system = `You are a resume content assistant. Always return strict JSON.`;
    const user = `Create 3 summary variants (formal, concise, friendly) for a ${level} ${occupation}. Target role: ${targetRole}. ZIP: ${zip}. 50–70 words each. Return:
{ "summaries": [{ "tone": "formal|concise|friendly", "text": "..." }] }
Context: skills/wins=${wins}`;
    const res = await generateJSON(system, user);
    setKit(k => ({ ...k, target_role: targetRole, target_zip: zip, summary_variants: res.summaries || [] }));
    setLoading(null);
  }

  async function makeKeywords() {
    setLoading("Keywords…");
    const system = `You are an ATS keyword assistant. Return JSON only.`;
    const user = `For a ${occupation} in ZIP ${zip} targeting ${targetRole}, list 30–40 keywords with type (skill|tool|domain|cert) and weight=1. Return:
{ "keywords": [{ "term": "...", "type": "skill|tool|domain|cert", "weight": 1 }] }`;
    const res = await generateJSON(system, user);
    setKit(k => ({ ...k, keyword_bank: res.keywords || [] }));
    setLoading(null);
  }

  async function makeSkills() {
    setLoading("Skills…");
    try {
      if (!isOpenAIConfigured()) {
        throw new Error('OpenAI not configured');
      }
      const system = `You are a resume skills curator. Return JSON only.`;
      const user = `For a ${level} ${occupation} targeting ${targetRole}, produce an extensive catalog (60-100) of skills with groups (Technical, Tools, Domain, Soft). Each item: {group, name, level:1-5}. Return:\n{ "skills": [{ "group": "Technical|Tools|Domain|Soft", "name": "...", "level": 1 }] }`;
      const res = await generateJSON(system, user);
      const catalog = Array.isArray(res.skills) ? res.skills.filter((s:any) => s && s.group && s.name) : [];
      if (!catalog.length) throw new Error('Empty catalog');
      setKit(k => ({ ...k, skills_catalog: catalog }));
    } catch (err:any) {
      // Fallback: derive a catalog from keywords + common sets
      const common = buildFallbackSkillsCatalog(occupation, targetRole, kit.keyword_bank || []);
      setKit(k => ({ ...k, skills_catalog: common }));
      alert('Using fallback skills catalog (AI unavailable).');
    } finally {
      setLoading(null);
    }
  }

  function buildFallbackSkillsCatalog(occ:string, role:string, keywords:Array<any>) {
    const lower = (occ || role || '').toLowerCase();
    const push = (arr:any[], group:string, name:string, level=3) => arr.push({ group, name, level });
    const out:any[] = [];
    // Seed from keywords
    keywords.slice(0, 40).forEach(k => {
      if (!k?.term) return;
      const term = String(k.term);
      const type = String(k.type || '').toLowerCase();
      const group = type.includes('tool') ? 'Tools' : type.includes('domain') ? 'Domain' : type.includes('cert') ? 'Domain' : 'Technical';
      push(out, group, term, 3);
    });
    // Heuristics by occupation
    if (lower.includes('developer') || lower.includes('engineer')) {
      ['Data Structures','Algorithms','System Design','APIs','Unit Testing','CI/CD'].forEach(n=>push(out,'Technical',n,3));
      ['React','Node.js','PostgreSQL','Docker','Git','Jest'].forEach(n=>push(out,'Tools',n,3));
      ['Web Applications','REST','Cloud'].forEach(n=>push(out,'Domain',n,3));
      ['Communication','Problem Solving','Collaboration'].forEach(n=>push(out,'Soft',n,3));
    }
    if (lower.includes('nurse') || lower.includes('health')) {
      ['Patient Assessment','Care Planning','Medication Administration','Vital Signs'].forEach(n=>push(out,'Technical',n,3));
      ['EMR/EHR','Epic','PPE','IV Pumps'].forEach(n=>push(out,'Tools',n,3));
      ['Acute Care','Outpatient','Inpatient'].forEach(n=>push(out,'Domain',n,3));
      ['Compassion','Teamwork','Attention to Detail'].forEach(n=>push(out,'Soft',n,3));
    }
    // Ensure coverage across groups
    const ensure = (group:string, names:string[]) => names.forEach(n=>{ if (!out.find(x=>x.group===group && x.name===n)) push(out, group, n, 3); });
    ensure('Soft', ['Leadership','Time Management','Adaptability','Stakeholder Communication']);
    // Deduplicate
    const seen = new Set();
    return out.filter(s => {
      const key = `${s.group}|${s.name}`.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).slice(0, 100);
  }

  function toggleSelectSkill(skill:{ group:string; name:string; level?:number }) {
    setKit(k => {
      const exists = k.skills.find((s:any) => s.group === skill.group && s.name === skill.name);
      if (exists) {
        return { ...k, skills: k.skills.filter((s:any) => !(s.group === skill.group && s.name === skill.name)) };
      }
      const level = typeof skill.level === 'number' ? skill.level : 3;
      return { ...k, skills: [...k.skills, { group: skill.group, name: skill.name, level }] };
    });
  }

  function selectAllVisibleSkills(list: Array<{ group:string; name:string; level?:number }>) {
    setKit(k => {
      const current = new Set(k.skills.map((s:any) => `${s.group}|${s.name}`));
      const additions = list.filter(s => !current.has(`${s.group}|${s.name}`)).map(s => ({ group: s.group, name: s.name, level: s.level ?? 3 }));
      return { ...k, skills: [...k.skills, ...additions] };
    });
  }

  function clearAllSelectedSkills() {
    setKit(k => ({ ...k, skills: [] }));
  }

  function filteredCatalog() {
    const q = skillsFilter.trim().toLowerCase();
    return (kit.skills_catalog || []).filter((s:any) => {
      const matchesText = !q || s.name.toLowerCase().includes(q) || s.group.toLowerCase().includes(q);
      const matchesGroup = !skillsGroupFilter || s.group === skillsGroupFilter;
      return matchesText && matchesGroup;
    });
  }

  function cleanupSentence(s:string){
    const t = (s || '').trim().replace(/\s+/g,' ');
    if (!t) return '';
    const noTrail = t.replace(/[.!?]+$/,'');
    const cap = noTrail.charAt(0).toUpperCase() + noTrail.slice(1);
    return `${cap}.`;
  }

  function makeStarSummaryLine(star:{ situation?:string; task?:string; action?:string; result?:string; metrics?:string }){
    const res = cleanupSentence(String(star.result || ''));
    const act = cleanupSentence(String(star.action || ''));
    const task = cleanupSentence(String(star.task || ''));
    const sit = cleanupSentence(String(star.situation || ''));
    const metrics = String(star.metrics || '').trim();
    // Build clear, grammatically sound clauses without awkward "by + past tense"
    const parts:string[] = [];
    if (res) parts.push(res);
    if (act) parts.push(`Actions: ${act}`);
    if (task) parts.push(`Objective: ${task}`);
    if (sit) parts.push(`Context: ${sit}`);
    let line = parts.join(' ');
    if (metrics) line += ` (${metrics})`;
    return line;
  }

  async function makeAchievements(starPayload) {
    setLoading("Achievements…");
    const system = `You turn STAR into resume bullets. Return JSON only.`;
    const user = `Turn this STAR into 5 bullets in neutral/impact/client-facing tones. <= 28 words each. Return:
{ "bullets": [{ "tone": "neutral|impact|client-facing", "text": "...", "method": "STAR" }] }
STAR: ${JSON.stringify(starPayload)}`;
    const res = await generateJSON(system, user);
    const items = (res.bullets || []).map(b => ({ bullet: b.text, tone: b.tone, method: "STAR" }));
    const summaryBullet = makeStarSummaryLine(starPayload);
    const withSummary = summaryBullet ? [{ bullet: summaryBullet, tone: 'neutral', method: 'STAR_SUMMARY' }, ...items] : items;
    setKit(k => ({ ...k, achievements: [...k.achievements, ...withSummary] }));
    setLoading(null);
  }

  async function saveKit() {
    setLoading("Saving…");
    // Replace with your user id retrieval if you store auth locally
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setLoading(null); alert("Please sign in."); return; }
    const payload = { ...kit, user_id: user.id };
    const { error } = await supabase.from("resume_kits").upsert(payload, { onConflict: "user_id" });
    setLoading(null);
    if (error) alert(error.message); else alert("Saved!");
  }

  function addStarEntry() {
    const trimmed = Object.fromEntries(Object.entries(starForm).map(([k, v]) => [k, (v || "").trim()])) as typeof starForm;
    if (!trimmed.situation || !trimmed.task || !trimmed.action || !trimmed.result) {
      alert("Please fill Situation, Task, Action, and Result.");
      return;
    }
    // If editing, update existing
    if (editingStarId) {
      setKit(k => ({
        ...k,
        star_entries: k.star_entries.map(e => e.id === editingStarId ? { ...e, ...trimmed } : e)
      }));
      setEditingStarId(null);
      setStarForm({ situation: "", task: "", action: "", result: "", metrics: "" });
      return;
    }
    const entry: StarEntry = {
      id: crypto.randomUUID(),
      situation: trimmed.situation,
      task: trimmed.task,
      action: trimmed.action,
      result: trimmed.result,
      metrics: trimmed.metrics,
      created_at: new Date().toISOString()
    };
    setKit(k => ({ ...k, star_entries: [entry, ...(k.star_entries || [])] }));
    setStarForm({ situation: "", task: "", action: "", result: "", metrics: "" });
  }

  function editStarEntry(id: string) {
    const entry = kit.star_entries.find(e => e.id === id);
    if (!entry) return;
    setEditingStarId(id);
    setStarForm({ situation: entry.situation, task: entry.task, action: entry.action, result: entry.result, metrics: entry.metrics || "" });
  }

  function deleteStarEntry(id: string) {
    setKit(k => ({ ...k, star_entries: k.star_entries.filter(e => e.id !== id) }));
    if (editingStarId === id) {
      setEditingStarId(null);
      setStarForm({ situation: "", task: "", action: "", result: "", metrics: "" });
    }
  }

  function moveStarEntry(id: string, direction: "up" | "down") {
    setKit(k => {
      const idx = k.star_entries.findIndex(e => e.id === id);
      if (idx < 0) return k;
      const target = direction === "up" ? idx - 1 : idx + 1;
      if (target < 0 || target >= k.star_entries.length) return k;
      const copy = [...k.star_entries];
      const [item] = copy.splice(idx, 1);
      copy.splice(target, 0, item);
      return { ...k, star_entries: copy };
    });
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header 
        user={user} 
        onAuthRequired={() => {}} // No-op since this is a premium feature
      />
      
      <main className="flex-1 pt-32 pb-16" style={{ paddingTop: 'calc(8rem + 30px)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Page Header */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center space-x-2 mb-4">
                <Icon name="FileText" size={32} className="text-primary" />
                <h1 className="text-3xl font-bold text-foreground">
                  Resume Content Kit
                </h1>
              </div>
              <p className="text-muted-foreground">
                Generate summaries, keywords, and achievement bullets tailored to your role.
              </p>
            </div>

            {/* Page Audio */}
            <div className="flex justify-center mb-8">
              <audio 
                src="/assets/audio/Resume_Builder.mp3" 
                controls 
                preload="metadata" 
                className="h-8 max-w-xs" 
              />
            </div>

            {/* AdSense - Banner for Free Users */}
            {!isPremium && (
              <div className="mb-8">
                <AdSense 
                  adSlot="1234567890"
                  adFormat="auto"
                  className="w-full"
                />
              </div>
            )}

            <div className="space-y-8">

      {/* Intake */}
      <section className="border rounded-xl p-5 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div><label className="block text-sm mb-1">Occupation</label>
            <input className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-primary focus:ring-2 focus:ring-blue-200" value={occupation} onChange={e=>setOccupation(e.target.value)} placeholder="e.g., Hairdresser, Registered Nurse" />
          </div>
          <div><label className="block text-sm mb-1">Experience Level</label>
            <select className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-primary focus:ring-2 focus:ring-blue-200" value={level} onChange={e=>setLevel(e.target.value)}>
              <option>Entry</option><option>Junior</option><option>Mid</option><option>Senior</option><option>Executive</option>
            </select>
          </div>
          <div><label className="block text-sm mb-1">Target Role</label>
            <input className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-primary focus:ring-2 focus:ring-blue-200" value={targetRole} onChange={e=>setTargetRole(e.target.value)} />
          </div>
          <div><label className="block text-sm mb-1">ZIP (target)</label>
            <input className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-primary focus:ring-2 focus:ring-blue-200" value={zip} onChange={e=>setZip(e.target.value)} placeholder="e.g., 44114" />
          </div>
        </div>
        <div>
          <label className="block text-sm mb-1">Top wins / skills (comma separated)</label>
          <input className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-primary focus:ring-2 focus:ring-blue-200" value={wins} onChange={e=>setWins(e.target.value)} placeholder="e.g., color correction, client retention +32%, OSHA compliance" />
        </div>

        <div className="flex gap-3">
          <button className="px-6 py-3 rounded-lg bg-primary hover:bg-blue-600 text-white font-medium transition-colors" onClick={makeSummaries}>Generate Summaries</button>
          <button className="px-6 py-3 rounded-lg border border-gray-300 hover:bg-gray-50 font-medium transition-colors" onClick={makeKeywords}>Generate Keywords</button>
          <button className="px-6 py-3 rounded-lg border border-gray-300 hover:bg-gray-50 font-medium transition-colors" onClick={makeSkills}>Generate Skills</button>
        </div>
        {loading && <p className="text-sm text-gray-500">{loading}</p>}
      </section>

      {/* Outputs */}
      <section className="grid md:grid-cols-2 gap-6">
        <div className="border rounded-xl p-5">
          <h3 className="font-semibold mb-2">Summary Variants</h3>
          <ul className="space-y-3">
            {kit.summary_variants.map((s, i) => (
              <li key={i} className="p-3 border rounded">{s.text}<div className="text-xs text-gray-500 mt-1">tone: {s.tone}</div></li>
            ))}
          </ul>
        </div>

        <div className="border rounded-xl p-5">
          <h3 className="font-semibold mb-2">Keywords</h3>
          <div className="flex flex-wrap gap-2">
            {kit.keyword_bank.map((k, i) => (
              <span key={i} className="px-2 py-1 rounded-full border text-sm">{k.term}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Skills */}
      <section className="border rounded-xl p-5">
        <h3 className="font-semibold mb-2">Skills</h3>
        <div className="flex flex-wrap gap-2 mb-3">
          <input className="border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Search skills..." value={skillsFilter} onChange={e=>setSkillsFilter(e.target.value)} />
          <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm" value={skillsGroupFilter || ''} onChange={e=>setSkillsGroupFilter(e.target.value || null)}>
            <option value="">All Groups</option>
            <option>Technical</option>
            <option>Tools</option>
            <option>Domain</option>
            <option>Soft</option>
          </select>
          <button className="px-3 py-2 rounded border text-sm" onClick={()=>selectAllVisibleSkills(filteredCatalog())}>Select All (visible)</button>
          <button className="px-3 py-2 rounded border text-sm" onClick={clearAllSelectedSkills}>Clear Selected</button>
          <button className="px-3 py-2 rounded border text-sm" onClick={makeSkills}>Regenerate Catalog</button>
        </div>

        {/* Catalog (click to select) */}
        <div className="grid md:grid-cols-2 gap-2 mb-4">
          {filteredCatalog().map((s:any, i:number) => {
            const isSelected = !!kit.skills.find((x:any) => x.group === s.group && x.name === s.name);
            return (
              <button key={`${s.group}-${s.name}-${i}`} className={`text-left p-2 rounded border text-sm ${isSelected ? 'bg-green-50 border-green-300' : ''}`} onClick={()=>toggleSelectSkill(s)}>
                <span className="font-medium">{s.group}:</span> {s.name} {typeof s.level === 'number' ? `(${s.level}/5)` : ''}
              </button>
            );
          })}
        </div>

        {/* Selected skills preview */}
        <div>
          <h4 className="font-medium mb-2">Selected</h4>
          {kit.skills.length === 0 ? (
            <p className="text-sm text-gray-500">No skills selected yet. Click skills above to add.</p>
          ) : (
            <ul className="grid md:grid-cols-2 gap-2">
              {kit.skills.map((s:any, i:number) => (
                <li key={i} className="p-2 border rounded text-sm"><span className="font-medium">{s.group}:</span> {s.name} ({s.level}/5)</li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Achievements (STAR) */}
      <section className="border rounded-xl p-5 space-y-4">
        <h3 className="font-semibold">Achievements (STAR)</h3>
        {/* STAR entries CRUD */}
        <div className="space-y-3">
          <div className="grid md:grid-cols-5 gap-3">
            <input className="border border-gray-300 rounded-lg px-4 py-3 focus:border-primary focus:ring-2 focus:ring-blue-200" placeholder="Situation" value={starForm.situation} onChange={e=>setStarForm(s=>({ ...s, situation: e.target.value }))} />
            <input className="border border-gray-300 rounded-lg px-4 py-3 focus:border-primary focus:ring-2 focus:ring-blue-200" placeholder="Task" value={starForm.task} onChange={e=>setStarForm(s=>({ ...s, task: e.target.value }))} />
            <input className="border border-gray-300 rounded-lg px-4 py-3 focus:border-primary focus:ring-2 focus:ring-blue-200" placeholder="Action" value={starForm.action} onChange={e=>setStarForm(s=>({ ...s, action: e.target.value }))} />
            <input className="border border-gray-300 rounded-lg px-4 py-3 focus:border-primary focus:ring-2 focus:ring-blue-200" placeholder="Result" value={starForm.result} onChange={e=>setStarForm(s=>({ ...s, result: e.target.value }))} />
            <input className="border border-gray-300 rounded-lg px-4 py-3 focus:border-primary focus:ring-2 focus:ring-blue-200" placeholder="Metrics (numbers)" value={starForm.metrics} onChange={e=>setStarForm(s=>({ ...s, metrics: e.target.value }))} />
            <button className="md:col-span-5 px-6 py-3 rounded-lg bg-primary hover:bg-blue-600 text-white font-medium transition-colors" onClick={addStarEntry}>
              {editingStarId ? 'Update STAR' : 'Add STAR'}
            </button>
          </div>

          {kit.star_entries.length > 0 && (
            <div className="space-y-2">
              {kit.star_entries.map((e) => (
                <div key={e.id} className="p-3 border rounded">
                  <div className="text-sm text-gray-700"><span className="font-medium">S:</span> {e.situation}</div>
                  <div className="text-sm text-gray-700"><span className="font-medium">T:</span> {e.task}</div>
                  <div className="text-sm text-gray-700"><span className="font-medium">A:</span> {e.action}</div>
                  <div className="text-sm text-gray-700"><span className="font-medium">R:</span> {e.result}</div>
                  {e.metrics && <div className="text-xs text-gray-500">metrics: {e.metrics}</div>}
                  <div className="flex gap-2 mt-2">
                    <button className="px-3 py-1 rounded border text-sm" onClick={() => moveStarEntry(e.id, 'up')}>Up</button>
                    <button className="px-3 py-1 rounded border text-sm" onClick={() => moveStarEntry(e.id, 'down')}>Down</button>
                    <button className="px-3 py-1 rounded border text-sm" onClick={() => editStarEntry(e.id)}>Edit</button>
                    <button className="px-3 py-1 rounded border text-sm text-red-600" onClick={() => deleteStarEntry(e.id)}>Delete</button>
                    <button className="px-3 py-1 rounded bg-green-600 text-white text-sm" onClick={() => makeAchievements({ situation: e.situation, task: e.task, action: e.action, result: e.result, metrics: e.metrics })}>Generate Bullets</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Existing generated bullets list */}
        <ul className="space-y-3">
          {kit.achievements.map((a, i) => <li key={i} className="p-3 border rounded">• {a.bullet}</li>)}
        </ul>
      </section>

      <ExportPanel kit={kit} />

              <div className="flex gap-3">
                <button className="px-6 py-3 rounded-lg border border-gray-300 hover:bg-gray-50 font-medium transition-colors" onClick={() => setKit(emptyKit)}>Reset</button>
                <button className="px-6 py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium transition-colors" onClick={saveKit}>Save Kit</button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

/** Minimal inline STAR form (kept here for brevity) */
function AchievementInline({ onCreate }) {
  const [situation, setS] = useState(""); const [task, setT] = useState("");
  const [action, setA] = useState(""); const [result, setR] = useState(""); const [metrics, setM] = useState("");
  return (
    <div className="grid md:grid-cols-5 gap-3">
      <input className="border border-gray-300 rounded-lg px-4 py-3 focus:border-primary focus:ring-2 focus:ring-blue-200" placeholder="Situation" value={situation} onChange={e=>setS(e.target.value)} />
      <input className="border border-gray-300 rounded-lg px-4 py-3 focus:border-primary focus:ring-2 focus:ring-blue-200" placeholder="Task" value={task} onChange={e=>setT(e.target.value)} />
      <input className="border border-gray-300 rounded-lg px-4 py-3 focus:border-primary focus:ring-2 focus:ring-blue-200" placeholder="Action" value={action} onChange={e=>setA(e.target.value)} />
      <input className="border border-gray-300 rounded-lg px-4 py-3 focus:border-primary focus:ring-2 focus:ring-blue-200" placeholder="Result" value={result} onChange={e=>setR(e.target.value)} />
      <input className="border border-gray-300 rounded-lg px-4 py-3 focus:border-primary focus:ring-2 focus:ring-blue-200" placeholder="Metrics (numbers)" value={metrics} onChange={e=>setM(e.target.value)} />
      <button className="md:col-span-5 px-6 py-3 rounded-lg bg-primary hover:bg-blue-600 text-white font-medium transition-colors" onClick={() => onCreate({ situation, task, action, result, metrics })}>
        Generate bullets
      </button>
    </div>
  );
}
